import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import EntryType from "../../api/models/entrytype";
import Program from "../../api/models/program";
import ProgramSchool from "../../api/models/programschool";
import School from "../../api/models/school";
import DistrictResponse from "../../api/responses/districtResponse";
import { getDistrict, getTherapistPeriodDistrictProgramOptions } from "../../api/services/districtService";
import { getEntryTypesForTaskType } from "../../api/services/entryTypeService";
import { EntryTypes } from "../../common/constants";
import moment from "moment";
import EntryState from "./types/entryState";
import DistrictProgramSelectOption from "./types/districtProgramSelectOption";
import DistrictsProgramOptionsResponse from "../../api/responses/districtProgramOptionsResponse";
import Select from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';

type Props = {
    entryState: EntryState;
    completed: (entry: EntryState) => void;
    cancel: (entry: EntryState) => void;
    onDistrictSelected: (option: DistrictProgramSelectOption) => void;
}

//holds the entry types that we can use, don't need to fetch it everytime we need it. 
let cachedEntryTypes: EntryType[];
const getEntryTypes = async () => {
    if (typeof cachedEntryTypes !== "undefined" && cachedEntryTypes.length > 0)
        return cachedEntryTypes;

    //fetch the entry types from the database
    var res = await getEntryTypesForTaskType();
    if (typeof res?.data?.entryTypes !== "undefined" && res?.data?.entryTypes.length > 0)
        cachedEntryTypes = res.data.entryTypes;
    else
        cachedEntryTypes = [];

    return cachedEntryTypes;
}

const fetchDistrictById = async (args: any[]) => {
    return await getDistrict(args[0].districtId);
}

const getDistrictProgramOptionsPromise = async (args: any) => {
    return getTherapistPeriodDistrictProgramOptions(args[0], args[1], args[2]);
}

const Step1 = ({ entryState: entryModel, completed, cancel, onDistrictSelected }: Props) => {
    //Commenting out. Unused and causing warning ~chriczko
    //const programIds = entryModel.districtProgramSelectOptions.map(x => x.programId);
    //const districtIds = entryModel.districtProgramSelectOptions.map(x => x.districtId);

    const [loading, setLoading] = useState<boolean>(true);
    //options
    const [schools, setSchools] = useState<School[]>();
    const [entryTypes, setEntryTypes] = useState<EntryType[]>();
    //selection
    const [districtId, setDistrictId] = useState<number>(-1);
    const [programId, setProgramId] = useState<number>(-1);
    const [schoolId, setSchoolId] = useState<number>(-1);
    const [entryTypeId, setEntryTypeId] = useState<number>(-1);

    const [esyStartDate, setEsyStartDate] = useState<Date>();
    const [esyEndDate, setEsyEndDate] = useState<Date>();
    const [isEsy, setIsEsy] = useState<boolean>(false);
    const [esySchool, setEsySchool] = useState<School>();
    const [districtProgramSelectOptions, setDistrictProgramSelectOptions] = useState<DistrictProgramSelectOption[]>(entryModel.districtProgramSelectOptions ?? []);

    const loadDistrictProgramOptions = useAsync({
        deferFn: getDistrictProgramOptionsPromise,
        onResolve: (response: DistrictsProgramOptionsResponse) => {
            if (response) {
                let options = [] as DistrictProgramSelectOption[];
                response.data?.districts.forEach((district, i) => options.push({ districtId: district.districtId ?? -1, label: district.name, programId: district.programId ?? -1, payrates: district.payrates ?? [] }));
                setDistrictProgramSelectOptions(options);
            }
        },
    });

    useEffect(() => {
        // //if there is only one district/program option, pre-select it in the dropdown option
        // if (entryModel.districtProgramSelectOptions.length === 1) {
        //     onDistrictOptionSelected(0); //select the first option 
        // }
        setDistrictId(-1);
        setProgramId(-1);
        setSchoolId(-1);
        setEntryTypeId(-1);

        loadDistrictProgramOptions.run(entryModel.startDate.toLocaleString(), entryModel.endDate.toLocaleString(), entryModel.disciplineId);

        loadSelectedDistrict();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (districtId > 0) {
            loadDistrictAsync.run({ districtId, programId });
        }
    }, [districtId, programId]);

    //load the entry type options
    useAsync({
        promiseFn: getEntryTypes,
        onResolve: (res) => setEntryTypes(res)
    });

    const getSchoolsFromDistrictResponse = (response: DistrictResponse) => {
        const schoolIdsForDistrictProgram: number[] = (response?.data?.district?.programs ?? [])
            .filter((p: Program) => (p.isVisible && p.programId === programId &&
                (p.programDisciplines.length === 0 || p.programDisciplines.some(d => d.disciplineId === entryModel.disciplineId))) ?? [])
            .map((p: Program) => p.programSchools)
            .reduce((prev: ProgramSchool[], curr: ProgramSchool[]) => prev.concat(curr), [])
            .map((ps: ProgramSchool) => ps.schoolId);

        const schools: School[] = (response?.data?.district?.schools ?? [])
            .filter(s => s.isVisible &&
                // Show either particular program schools if programId exists, or show all schools in district if no programId exists
                ((programId > 0) ? schoolIdsForDistrictProgram.indexOf(s.schoolId ?? -1) > -1 : true)
            ) ?? [];

        return schools;
    }

    const loadDistrictAsync = useAsync({
        deferFn: fetchDistrictById,
        onResolve: (response: DistrictResponse) => {
            if (response) {
                //Update our entryState
                entryModel.district = response.data.district;

                //Determine if this is an ESY date AND if we are currently in an ESY period
                const isValidEsyDate = moment(response.data.district.esyStartDate).isValid() && moment(response.data.district.esyStartDate).isBefore(moment());
                const isEsyEnd = !response.data.district.esyEndDate ? false : moment(response.data.district.esyEndDate).isValid();
                const isValidEsyEndDate = isEsyEnd ? moment().isBefore(response.data.district.esyEndDate) : true;
                setEsyStartDate(response.data.district.esyStartDate);
                setEsyEndDate(response.data.district.esyEndDate);

                //If we are in an ESY period, set the schools array to be that of the ESY school for this district
                if (isValidEsyDate && isValidEsyEndDate) {
                    //commenting this out as per Jake's request
                    //const schools = [response.data.district.esySchool!];

                    let schools: School[] = [];
                    //add the ESY school as first option. It's filtered out because it's not visible but we want to see it here
                    schools.push(response.data.district.esySchool!);
                    //add the rest of the schools
                    schools = schools.concat(getSchoolsFromDistrictResponse(response));


                    setSchools(schools);
                    setIsEsy(true);
                    setSchoolId(response?.data?.district?.esySchool?.schoolId!);
                    setEntryTypeId(-1);
                    setEsySchool(schools[0]);
                    entryModel.school = schools[0];
                    entryModel.isEsy = true;
                    return;
                }

                //Otherwise, use the normal flow
                entryModel.isEsy = false;
                const schools = getSchoolsFromDistrictResponse(response);
                setEsySchool(undefined);
                setIsEsy(false);
                setSchools(schools);
                setEntryTypeId(-1);
            }
        },
    });

    const loadSelectedDistrict = async () => {
        // if (entryModel.disciplineId && ((entryModel.districtId ?? -1) > 0)) {
        //     setDistrictId(entryModel.districtId ?? -1);
        //     setProgramId(entryModel.programId ?? -1);
        //     setSchoolId(entryModel.schoolId ?? -1);
        // }
        setLoading(false);
    }

    const onDistrictOptionSelected = async (index: number) => {
        var option = districtProgramSelectOptions[index];

        //If no option was found then we have a problem
        if (!option) {
            console.error(`No option found in onDistrictOptionSelected at index ${index}`);
            return;
        }
        onDistrictSelected(option);
        setDistrictId(option.districtId);
        setProgramId(option.programId ?? -1);
        setSchoolId(-1);
    }

    // returns the index of the districtOptions based on the user's selection
    const getSelectedDistrictOption = () => {
        for (let index = 0; index < districtProgramSelectOptions.length; index++) {
            const element = districtProgramSelectOptions[index];
            if (element.programId === programId && element.districtId === districtId) {
                return index;
            }
        }
        return -1;
    }

    const Placeholder_School = () => {
        if (loading)
            return "Loading";
        if (!districtId || districtId === -1)
            return "Select District";
        if (!schools || schools.length === 0)
            return "No school for selected district";

        return "Select school";
    }

    const Placeholder_EntryType = () => {
        if (loading)
            return "Loading";
        if (!districtId || districtId === -1)
            return "Select District";

        return "Select Entry Type";
    }

    //if all fields are selected then we can mark this step as completed
    useEffect(() => {
        if (districtId > 0 && entryTypeId > 0) {
            //task type Non-Student allows users to not select a school
            if (entryTypeId !== EntryTypes.NonStudent && schoolId <= 0)
                return;

            const district = entryModel.districts?.find(d => d.districtId === districtId);

            entryModel.allowsDistrictTime = district?.allowsDistrictTime ?? false;
            entryModel.districtId = districtId;
            entryModel.districtName = district?.name!;
            entryModel.dailyAllowance = district?.dailyAllowance ?? 0;
            entryModel.dailyOverageAllowance = district?.dailyOverageAllowance ?? 0;
            entryModel.unlimitedOverageAllowed = district?.unlimitedOverageAllowed ?? false;
            entryModel.programId = programId;
            entryModel.programName = district?.programs?.find(d => d.programId === programId)?.name!;
            entryModel.schoolId = schoolId;
            entryModel.schoolName = schools?.find(d => d.schoolId === schoolId)?.name!;
            entryModel.entryTypeId = entryTypeId;
            entryModel.entryTypeName = entryTypes?.find(d => d.entryTypeId === entryTypeId)?.name;
            //clear out selected fields
            entryModel.taskTypeId = -1;
            entryModel.studentId = -1;
            entryModel.billToId = -1;
            entryModel.selectedStudents = [];
            completed(entryModel);
        }
    });

    return (
        <Fragment >
            <div className='px-4 py-3 pt-5 pb-4 mt-1 bg-white rounded-lg sm:p-6 sm:pb-4'>

                <label>District</label>
                <Select
                    isDisabled={loading}
                    onChange={(option: any) => { if (option) onDistrictOptionSelected(option.value); }}
                    value={
                      (() => {
                        const idx = getSelectedDistrictOption();
                        const opts = (districtProgramSelectOptions ?? []).map((option, i) => ({ value: i, label: option.label }));
                        return idx === -1 ? null : opts.find(o => o.value === idx) ?? null;
                      })()
                    }
                    styles={graySelectStyles}
                    isSearchable={false}
                    placeholder="Select District"
                    options={(districtProgramSelectOptions ?? []).map((option, i) => ({ value: i, label: option.label }))}
                />

                <label>School</label>
                <Select
                    value={
                      schoolId === -1 ? null : (schools ?? []).sort((a, b) => a.name.localeCompare(b.name)).map((p) => ({ value: p.schoolId, label: p.name })).find(o => o.value === schoolId) ?? null
                    }
                    onChange={(option: any) => setSchoolId(option ? Number(option.value) : -1)}
                    isDisabled={loading || (districtId === -1)}
                    styles={graySelectStyles}
                    isSearchable={false}
                    placeholder={Placeholder_School()}
                    options={(schools ?? []).sort((a, b) => a.name.localeCompare(b.name)).map((p) => ({ value: p.schoolId, label: p.name }))}
                />

                <label>Entry Type</label>
                <Select
                    value={
                      entryTypeId === -1 ? null : (entryTypes ?? []).sort((a, b) => a.name.localeCompare(b.name)).map((t) => ({ value: t.entryTypeId, label: t.name })).find(o => o.value === entryTypeId) ?? null
                    }
                    onChange={(option: any) => setEntryTypeId(option ? Number(option.value) : -1)}
                    isDisabled={loading || (districtId === -1)}
                    styles={graySelectStyles}
                    isSearchable={false}
                    placeholder={Placeholder_EntryType()}
                    options={(entryTypes ?? []).sort((a, b) => a.name.localeCompare(b.name)).map((t) => ({ value: t.entryTypeId, label: t.name }))}
                />
            </div>
            <div className='px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse'>
                <button
                    type='button'
                    className='inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 transition duration-150 ease-in-out bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
                    onClick={() => { cancel(entryModel); }}
                >
                    Cancel
                </button>
            </div>
        </Fragment>
    )
}

export default Step1;